import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, Send, X, Scissors } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getSessionId = () => {
  let id = localStorage.getItem("bany_chat_session");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("bany_chat_session", id);
  }
  return id;
};

const WELCOME = "Ciao! Sono l'assistente di BANY. Chiedimi di prezzi, orari o come prenotare. / Hi! Ask me anything about prices, hours or booking.";

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionId = useRef(getSessionId());
  const listRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/chat/${sessionId.current}`)
      .then((r) => r.json())
      .then((d) => setMessages(d.messages || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text }),
      });
      if (!res.ok || !res.body) throw new Error("bad response");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      const append = (delta) =>
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + delta };
          return copy;
        });
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (payload === "[DONE]") continue;
          const data = JSON.parse(payload);
          if (data.delta) append(data.delta);
          if (data.error) append(data.error);
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Ops, non riesco a rispondere ora. Riprova tra poco." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <motion.button
        data-testid="chat-open-button"
        onClick={() => setOpen((o) => !o)}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.95 }}
        aria-label="Apri assistente"
        className="fixed bottom-5 right-5 z-[60] w-14 h-14 rounded-full bg-[#0B1F3A] text-[#F7F2EC] shadow-[0_10px_40px_rgba(11,31,58,0.35)] flex items-center justify-center"
      >
        {open ? <X className="w-6 h-6" strokeWidth={2} /> : <MessageCircle className="w-6 h-6" strokeWidth={2} />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-5 z-[60] w-[calc(100vw-2.5rem)] sm:w-[380px] h-[min(560px,calc(100vh-8rem))] rounded-2xl border border-[rgba(11,31,58,0.3)] bg-[#FFFFFF]/95 backdrop-blur-xl shadow-2xl flex flex-col overflow-hidden"
          >
            <div className="px-5 py-4 border-b border-[rgba(11,31,58,0.2)] flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-[#0B1F3A]/15 flex items-center justify-center">
                <Scissors className="w-4 h-4 text-[#0B1F3A]" strokeWidth={1.8} />
              </div>
              <div>
                <p className="font-display font-semibold text-sm leading-tight">Bany Assistant</p>
                <p className="text-[11px] text-[#6F6466] font-mono-alt uppercase tracking-widest">Online · IT / EN</p>
              </div>
            </div>

            <div ref={listRef} data-testid="chat-messages" className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              <Bubble role="assistant" content={WELCOME} />
              {messages.map((m, i) => (
                <Bubble key={i} role={m.role} content={m.content} typing={busy && i === messages.length - 1 && !m.content} />
              ))}
            </div>

            <form onSubmit={send} className="p-3 border-t border-[rgba(11,31,58,0.2)] flex gap-2">
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Scrivi un messaggio…"
                maxLength={2000}
                className="flex-1 bg-[#F1EAE2] rounded-full px-4 py-2.5 text-sm outline-none border border-transparent focus:border-[rgba(11,31,58,0.5)] transition-colors placeholder:text-[#9A8F90]"
              />
              <button
                data-testid="chat-send-button"
                type="submit"
                disabled={busy || !input.trim()}
                aria-label="Invia"
                className="w-10 h-10 rounded-full bg-[#0B1F3A] text-[#F7F2EC] flex items-center justify-center disabled:opacity-40 transition-opacity"
              >
                <Send className="w-4 h-4" strokeWidth={2} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const Bubble = ({ role, content, typing }) => {
  const user = role === "user";
  return (
    <div className={`flex ${user ? "justify-end" : "justify-start"}`}>
      <div
        data-testid={`chat-bubble-${role}`}
        className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap rounded-2xl ${
          user ? "bg-[#0B1F3A] text-[#F7F2EC] rounded-br-md" : "bg-[#F1EAE2] text-[#1A1416] rounded-bl-md border border-[rgba(0,0,0,0.05)]"
        }`}
      >
        {typing ? <span className="chat-typing"><i /><i /><i /></span> : content}
      </div>
    </div>
  );
};

export default ChatWidget;
